import { useEffect, useMemo, useState } from "react";
import { originalCatalog } from "@marshmallow-duel/game-data";
import { builtInMaps, mdmToArenaMap } from "@marshmallow-duel/simulation";

import { listMaps, recordMapPlay } from "../game/api-client";
import { localizedMapName } from "../map-names";
import { OriginalDialog } from "./OriginalDialog";

import type { ArenaMap, Language, MapLibraryRecord, PlayerCount } from "@marshmallow-duel/simulation";
import type { TranslationKey } from "../i18n";

interface MapSelectionProps {
  readonly language: Language;
  readonly playerCount: PlayerCount;
  readonly t: (key: TranslationKey) => string;
  readonly onSelect: (map: ArenaMap) => void;
  readonly onClose: () => void;
}

export const MapSelection = ({ language, playerCount, t, onSelect, onClose }: MapSelectionProps) => {
  const maps = builtInMaps(playerCount);
  const [records, setRecords] = useState<readonly MapLibraryRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const libraryMaps = useMemo(() => records.map((record) => ({ record, map: mdmToArenaMap(record.map, `library:${record.id}`) })), [records]);
  useEffect(() => {
    let active = true;
    setLoading(true);
    void listMaps({ playerCount }).then((next) => active && setRecords(next)).catch(() => active && setRecords([])).finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [playerCount]);
  const selectRandom = () => {
    const allMaps = [...maps, ...libraryMaps.map(({ map }) => map)];
    const map = allMaps[Math.floor(Math.random() * allMaps.length)];
    if (map) onSelect(map);
  };
  return (
    <OriginalDialog title={t("maps.title")} onClose={onClose} wide>
      <div className="map-toolbar">
        <strong>{t("maps.original")}</strong>
        <button type="button" className="small-original-button" onClick={selectRandom}>{t("common.random")}</button>
      </div>
      <div className="map-grid">
        {maps.map((map) => {
          const thumbnail = originalCatalog.sprites[`spr_room_${map.id.replace("rm_arena_", "")}`]?.frames[0]?.url;
          return (
            <button key={map.id} type="button" className="map-card" onClick={() => onSelect(map)}>
              {thumbnail ? <img src={thumbnail} alt="" /> : <span className="map-placeholder" />}
              <span>{localizedMapName(language, map.id, map.name)}</span>
            </button>
          );
        })}
      </div>
      <div className="map-toolbar map-toolbar--library"><strong>{t("maps.library")}</strong></div>
      {loading ? <p className="map-library-note">{t("maps.loading")}</p> : libraryMaps.length === 0 ? <p className="map-library-note">{t("maps.empty")}</p> : (
        <div className="map-grid">
          {libraryMaps.map(({ record, map }) => (
            <button key={record.id} type="button" className="map-card community-map-card" onClick={() => { void recordMapPlay(record.id).catch(() => undefined); onSelect(map); }}>
              <span className="map-placeholder"><b>MDM</b><small>{record.playerCount}P</small></span>
              <span>{record.name}<small>{t("maps.by")} {record.authorName}</small></span>
            </button>
          ))}
        </div>
      )}
    </OriginalDialog>
  );
};
